// src/components/Layout/MiniInventoryDisplay.js
import React, { useState, useEffect } from 'react';
import styles from './MiniInventoryDisplay.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';
import ItemTooltip from '../ItemTooltip';

const MiniInventoryDisplay = ({ playerData, onEquipItem, onUseItem, onSellItem, onToggleItemLock, isItemLocked, onSellAllUnlockedItems, shopContext = false }) => {
  const currentInventory = playerData.inventory || {};
  // Remove the arbitrary item limit - show all items in both contexts
  // We'll rely on CSS scrolling to handle overflow

  const [selectedItemId, setSelectedItemId] = useState(null);

  // Equipment slot names for 3x4 grid
  const equipmentSlotNames = [ 
    "Back", "Helmet", "Necklace",
    "MainHand", "Chest", "OffHand",
    "Gloves", "Pants", "Boots",
    "Ring1", "Belt", "Ring2"
  ];

  useEffect(() => {
    const inventorySource = playerData.inventory || {};
    if (selectedItemId) {
        const item = inventorySource[selectedItemId];
        if (!item || (typeof item === 'number' && item <= 0)) {
            setSelectedItemId(null);
        }
    }
  }, [playerData.inventory, selectedItemId, shopContext]);


  const handleItemClick = (itemId) => {
    setSelectedItemId(itemId);
  };

  const selectedItemInstance = selectedItemId ? currentInventory[selectedItemId] : null;
  let itemKeyForBaseData = selectedItemId;

  if (selectedItemInstance && typeof selectedItemInstance === 'object' && selectedItemInstance.key) {
    itemKeyForBaseData = selectedItemInstance.key;
  }
  const selectedItemBaseData = selectedItemId ? ItemData[itemKeyForBaseData] : null;

  const itemsForGrid = Object.entries(currentInventory)
    .map(([idOrKey, itemDataOrCount]) => {
      if (typeof itemDataOrCount === 'object' && itemDataOrCount !== null) {
        const isEquipped = Object.values(playerData.equipment).includes(idOrKey);
        // In shop, show equipped (dimmed) but don't filter out. They can't be sold from button anyway.
        if (isEquipped && !shopContext && onEquipItem) return null; // Only filter if not in shop & equip func exists
        return { ...itemDataOrCount, id: idOrKey, isStackable: false, key: itemDataOrCount.key };
      } else if (typeof itemDataOrCount === 'number' && itemDataOrCount > 0) {
        const baseData = ItemData[idOrKey];
        if (!baseData) {
          console.warn(`[MiniInventoryDisplay] ItemData missing for key: ${idOrKey}`);
          return null;
        }
        return { ...baseData, id: idOrKey, key: idOrKey, quantity: itemDataOrCount, isStackable: true };
      }
      return null;
    })
    .filter(item => item !== null);

  const getSlotKey = (slotName) => {
    return slotName.charAt(0).toLowerCase() + slotName.slice(1);
  };

  return (
    <div className={styles.miniInventoryContainer}>
      <div className={styles.titleBar}>
        <h4 className={styles.title}>{shopContext ? "Your Items (Sell)" : "Quick Inventory"}</h4>
        {shopContext && onSellAllUnlockedItems && (
          <button 
            className={styles.sellAllButton}
            onClick={onSellAllUnlockedItems}
            title="Sell all unlocked items with value"
          >
            💰 Sell All Unlocked
          </button>
        )}
      </div>
      <div className={styles.horizontalLayout}>
        <div className={styles.itemsPanel}>
          <div className={styles.itemGrid}>
            {itemsForGrid.map(item => {
                // Check if unique item ID is in equipment values
                const isEquipped = typeof item === 'object' && item.id && Object.values(playerData.equipment).includes(item.id);
                return (
                  <ItemTooltip
                    key={item.id} // item.id is unique for instances, and is the key for stackables
                    itemKey={item.key}
                    itemInstance={item}
                    onEquipItem={!shopContext ? onEquipItem : null}
                    onUseItem={onUseItem}
                    onSellItem={shopContext ? onSellItem : null}
                    canEquip={!isEquipped}
                    canSell={shopContext ? !isEquipped : false}
                    disabled={isEquipped && shopContext} // Disable tooltip if equipped in shop
                  >
                    <div
                      className={`${styles.inventoryItem} ${selectedItemId === item.id ? styles.selectedItem : ''} ${isEquipped && shopContext ? styles.equippedInShop : ''}`}
                      onClick={() => handleItemClick(item.id)}
                      title={isEquipped && shopContext ? `${item.name || ItemData[item.key]?.name} (Equipped - Cannot Sell)` : undefined}
                    >
                      <img src={getItemImageSrc(item.key)} alt={item.name || ItemData[item.key]?.name} className={styles.itemIcon} />
                      {item.isStackable && item.quantity > 1 && (
                        <span className={styles.itemQuantity}>{item.quantity}</span>
                      )}
                      {!item.isStackable && item.durability !== null && item.durability !== undefined && (
                        <span className={`${styles.itemDurability} ${item.durability <= 0 ? styles.broken : ''}`}>
                          {item.durability}/{item.maxDurability}
                        </span>
                      )}
                      {isItemLocked && isItemLocked(item.id) && (
                        <span className={styles.lockIcon}>🔒</span>
                      )}
                    </div>
                  </ItemTooltip>
                );
            })}
            {/* Only show empty slots in wilderness context and only if we have very few items */}
            {!shopContext && itemsForGrid.length < 12 && [...Array(Math.max(0, 12 - itemsForGrid.length))].map((_, i) => (
              <div key={`empty-${i}`} className={`${styles.inventoryItem} ${styles.inventoryItemEmpty}`}></div>
            ))}
          </div>
        </div>

        <div className={styles.detailsPanel}>
          {selectedItemBaseData && (
            <div className={styles.miniItemDetails}>
              <div className={styles.detailsHeader}>
                <img src={getItemImageSrc(selectedItemBaseData.key || selectedItemId)} alt={selectedItemBaseData.name} className={styles.detailsIcon} />
                <span className={styles.detailsName}>{selectedItemBaseData.name}</span>
              </div>
              <p className={styles.detailsDescription}>{selectedItemBaseData.description || "No description."}</p>
              
              {typeof selectedItemInstance === 'object' && selectedItemBaseData.maxDurability !== undefined && (
                <div className={styles.detailsDurability}>
                  Condition: {selectedItemInstance.durability} / {selectedItemInstance.maxDurability}
                </div>
              )}

              <div className={styles.detailsActions}>
                {shopContext && onSellItem && selectedItemBaseData.goldValue > 0 && (
                  <>
                    <button
                      onClick={() => onSellItem(selectedItemId, false)}
                      className={`${styles.button} ${styles.sellButton}`}
                      disabled={
                        (typeof selectedItemInstance === 'object' && selectedItemInstance.id && Object.values(playerData.equipment).includes(selectedItemInstance.id)) ||
                        (isItemLocked && isItemLocked(selectedItemId))
                      }
                      title={isItemLocked && isItemLocked(selectedItemId) ? "Item is locked from selling" : undefined}
                    >
                      Sell 1 ({(selectedItemBaseData.goldValue || 0)} G)
                    </button>
                    {selectedItemBaseData.stackable && typeof selectedItemInstance === 'number' && selectedItemInstance > 1 && (
                      <button
                        onClick={() => onSellItem(selectedItemId, true)} // selectedItemId is the key for stackables
                        className={`${styles.button} ${styles.sellAllButton}`}
                        disabled={isItemLocked && isItemLocked(selectedItemId)}
                        title={isItemLocked && isItemLocked(selectedItemId) ? "Item is locked from selling" : undefined}
                      >
                        Sell All ({selectedItemInstance}) for ({ (selectedItemBaseData.goldValue || 0) * selectedItemInstance } G)
                      </button>
                    )}
                  </>
                )}
                {!shopContext && selectedItemBaseData.type === 'consumable' && onUseItem && (
                  <button 
                    onClick={() => onUseItem(selectedItemId)}
                    className={`${styles.button} ${styles.useButton}`}
                  >
                    Use
                  </button>
                )}
                {!shopContext && typeof selectedItemInstance === 'object' && selectedItemBaseData.slotType && onEquipItem && (
                  <button 
                    onClick={() => onEquipItem(selectedItemId)} 
                    className={`${styles.button} ${styles.equipButton}`}
                    disabled={selectedItemInstance.durability !== undefined && selectedItemInstance.durability <= 0}
                  >
                    Equip
                  </button>
                )}
                {onToggleItemLock && selectedItemId && (
                  <button 
                    onClick={() => onToggleItemLock(selectedItemId)}
                    className={`${styles.button} ${isItemLocked && isItemLocked(selectedItemId) ? styles.unlockButton : styles.lockButton}`}
                  >
                    {isItemLocked && isItemLocked(selectedItemId) ? '🔓 Unlock' : '🔒 Lock'}
                  </button>
                )}
              </div>
            </div>
          )}
          {!selectedItemBaseData && (
            <div className={styles.noItemSelectedMessage}>
                Select an item to see details.
            </div>
          )}
        </div>

        {/* Equipment Panel - Third Column */}
        <div className={styles.equipmentPanel}>
          <div className={styles.equipmentSection}>
            <h5 className={styles.equipmentTitle}>Equipment</h5>
            <div className={styles.equipmentGrid}>
              {equipmentSlotNames.map(slotName => {
                const slotKey = getSlotKey(slotName); 
                const equippedItemId = playerData.equipment[slotKey];
                const itemInstance = equippedItemId ? playerData.inventory[equippedItemId] : null;
                const itemBaseKey = itemInstance ? itemInstance.key : null;
                const itemBaseData = itemBaseKey ? ItemData[itemBaseKey] : null;

                let isDisabled = false;
                let disabledReason = "";
                if (slotKey === 'offHand') {
                    const mainHandItemInstance = playerData.equipment.mainHand ? playerData.inventory[playerData.equipment.mainHand] : null;
                    const mainHandBase = mainHandItemInstance ? ItemData[mainHandItemInstance.key] : null;
                    if (mainHandBase && mainHandBase.handedness === 'twoHanded') {
                        isDisabled = true;
                        disabledReason = "2H";
                    }
                }

                return (
                  <ItemTooltip
                    key={slotKey}
                    itemKey={itemBaseKey}
                    itemInstance={itemInstance}
                    showStats={true}
                    disabled={!itemInstance || isDisabled}
                  >
                    <div
                      className={`${styles.equipmentSlot} ${itemInstance ? styles.equipped : styles.empty} ${isDisabled ? styles.disabled : ''}`}
                      title={isDisabled ? disabledReason : (!itemInstance ? `Empty ${slotName} slot` : undefined)}
                    >
                      {isDisabled ? (
                          <span className={styles.slotLabel}>{disabledReason}</span>
                      ) : itemInstance && itemBaseData ? (
                        <>
                          <img
                            src={getItemImageSrc(itemBaseKey)}
                            alt={itemBaseData.name}
                            className={styles.equippedItemIcon}
                          />
                          {itemInstance.durability !== null && itemInstance.durability !== undefined && (
                              <span className={`${styles.equippedItemDurability} ${itemInstance.durability <= 0 ? styles.broken : ''}`}>
                                  {itemInstance.durability}/{itemInstance.maxDurability}
                              </span>
                          )}
                        </>
                      ) : (
                        <span className={styles.slotLabel}>{slotName}</span>
                      )}
                    </div>
                  </ItemTooltip>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniInventoryDisplay;